import { StateCreator } from 'zustand';
import { PartitionFileObjectsTypeQR, PartitionFileObjectsType } from '@utils';
import {
  HIRE_BIKE_COMPANIES,
  HIRE_BIKE_SCOOTERS,
  HireBikeCompanies,
  HireScooterCompanies,
} from '@bikes/constants/hireCompanies';
import { AssetType, HighwaysCarriagewaysFeatureAttributes } from '@types';
import { BinaryOptionsType } from '@highways/store/storeSlices/highwaysDetails';

export enum BikesType {
  Bikes = 'bikes',
  HireBikes = 'hire_bikes',
  HireScooters = 'hire_scooters',
}

export enum BikesTypeLabel {
  bikes = 'bike',
  hire_bikes = 'hire bike',
  hire_scooters = 'hire scooter',
}

export interface HireCompaniesOptions {
  title: HireBikeCompanies | HireScooterCompanies;
  description?: string;
  image: string;
}

export type CaseIDProps = {
  case_reference: string;
  sla_text: string;
};

export const emptyServiceAssetDetails: HighwaysCarriagewaysFeatureAttributes = {
  site_class_code: '',
  site_class_name: '',
  central_asset_id: '',
  contract_area_code: '',
  feature_type_code: '',
  site_name: '',
  layer: '' as AssetType,
};

export interface ServiceLocationDetailsObjectType {
  address_line_1: string;
  address_line_2: string;
  country: string;
  county: string;
  easting: string | null;
  latitude: number | null;
  longitude: number | null;
  northing: string | null;
  postcode: string;
  town_city: string;
  UPRN: string;
  USRN: string;
  full_address?: string;
}

// This is a placeholder the states will be updated during development.
export interface BikesDetailsSlice {
  bikesType: BikesType | null;
  updateBikesType: (bikesType: BikesType) => void;
  bikesTypeLabel: BikesTypeLabel | null;
  hireCompaniesOptions: HireCompaniesOptions[] | null;
  hireCompany: HireBikeCompanies | null;
  updateHireCompany: (hireCompany: HireBikeCompanies | null) => void;
  confirmation: CaseIDProps | undefined;
  uploadedImages: string[];
  serviceAssetDetails: HighwaysCarriagewaysFeatureAttributes;
  ownershipType: string;
  obstructionType: string;
  updateObstructionType: (obstructionType: string) => void;
  updateOwnershipType: (ownerShipType: string) => void;
  tellUsMoreText: string;
  updateTellUsMoreText: (tellUsMoreText: string) => void;
  uploadedImagesFileName: string[];
  uploadedImagesFileType: string[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  serviceLocationDetails: ServiceLocationDetailsObjectType;
  isPhotoLocationStored: boolean;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  fileWithLoadedCoordinates: string;
  updateServiceAssetDetails: (
    serviceAssetDetails: HighwaysCarriagewaysFeatureAttributes,
  ) => void;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationDetailsObjectType,
  ) => void;
  updateUploadedImagesBlobs: (
    uploadedImagesBlob: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
  fileDetailsObjects: { name: string; path: string }[];

  // for QR code photo
  uploadedImagesQR: string[];
  uploadedImagesFileNameQR: string[];
  uploadedImagesFileTypeQR: string[];
  uploadedImagesBlobQR: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  updateUploadedImagesQR: (
    uploadedImagesQR: PartitionFileObjectsTypeQR,
  ) => void;
  serviceLocationDetailsQR: ServiceLocationDetailsObjectType;
  isPhotoLocationStoredQR: boolean;
  updateIsPhotoLocationStoredQR: (isPhotoLocationStoredQR: boolean) => void;
  fileWithLoadedCoordinatesQR: string;
  updateFileWithLoadedCoordinatesQR: (
    fileWithLoadedCoordinatesQR: string,
  ) => void;
  updateServiceLocationDetailsQR: (
    serviceLocationDetailsQR: ServiceLocationDetailsObjectType,
  ) => void;
  updateUploadedImagesBlobsQR: (
    uploadedImagesBlobQR: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
  fileDetailsObjectsQR: { name: string; path: string }[];
  updateConfirmation: (case_id: CaseIDProps | undefined) => void;
  receiveConfirmationEmail: string;
  updateReceiveConfirmationEmail: (receiveConfirmationEmail: string) => void;
  caseReferenceId: string;
  slaText: string;
  updateCaseReferenceId: (caseReferenceId: string) => void;
  updateSlaText: (slaText: string | undefined) => void;
  wantConfirmationEmail: BinaryOptionsType;
  resetBikesDetails: () => void;
  resetConfirmationInformation: () => void;
}

// fill in mock data for testing -> to remove in production
export const emptyServiceLocationDetails = {
  address_line_1: '',
  address_line_2: '',
  country: '',
  county: '',
  easting: null,
  latitude: null,
  longitude: null,
  northing: null,
  postcode: '',
  town_city: '',
  UPRN: '',
  USRN: '',
};

export const createBikesDetailsSlice: StateCreator<
  BikesDetailsSlice,
  [],
  [],
  BikesDetailsSlice
> = (set, get) => ({
  bikesType: null,
  bikesTypeLabel: null,
  hireCompaniesOptions: null,
  hireCompany: null,
  updateHireCompany: (hireCompany) => set(() => ({ hireCompany })),
  confirmation: undefined,
  updateBikesType: (bikesType: BikesType) => {
    const bikesTypeLabel = bikesType ? BikesTypeLabel[bikesType] : null;
    set({ bikesType, bikesTypeLabel });

    switch (bikesType) {
      case BikesType.HireBikes:
        set(() => {
          return {
            hireCompaniesOptions: HIRE_BIKE_COMPANIES,
          };
        });
        break;
      case BikesType.HireScooters:
        set(() => {
          return {
            hireCompaniesOptions: HIRE_BIKE_SCOOTERS,
          };
        });
        break;

      default:
        set(() => {
          return {
            hireCompaniesOptions: [],
          };
        });
        break;
    }
  },
  uploadedImages: [],
  uploadedImagesFileName: [],
  uploadedImagesFileType: [],
  uploadedImagesBlob: [],
  fileDetailsObjects: [],
  ownershipType: '',
  obstructionType: '',
  tellUsMoreText: '',
  updateTellUsMoreText: (tellUsMoreText: string) =>
    set(() => ({ tellUsMoreText })),
  updateObstructionType: (obstructionType: string) =>
    set(() => ({ obstructionType })),
  updateOwnershipType: (ownershipType: string) =>
    set(() => ({ ownershipType })),
  updateUploadedImages: (uploadedFileParts: PartitionFileObjectsType) => {
    const {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      geoDataArrays,
    } = uploadedFileParts;
    const fileDetailsObjects = uploadedImagesFileName.map(
      (filename, index) => ({
        name: filename,
        path: uploadedImagesBlob[index].blob_uri,
      }),
    );
    const { serviceLocationDetails, updateFileWithLoadedCoordinates } = get();
    // update the service location details with the first geo data found
    if (
      !serviceLocationDetails.latitude &&
      !serviceLocationDetails.longitude &&
      geoDataArrays.length > 0
    ) {
      geoDataArrays.some((geoData) => {
        if (
          geoData.latitude !== null &&
          geoData.longitude !== null &&
          geoData.fileName !== null
        ) {
          updateFileWithLoadedCoordinates(geoData.fileName);
          set((state) => ({
            serviceLocationDetails: {
              ...state.serviceLocationDetails,
              latitude: geoData.latitude,
              longitude: geoData.longitude,
            },
          }));
          // break the loop if the first geo data is found
          return true;
        }
        // continue the loop
        return false;
      });
    }
    // update the service location details with the first geo data found
    set({ uploadedImages });
    set({ uploadedImagesFileName });
    set({ uploadedImagesFileType });
    set({ uploadedImagesBlob });
    set({ fileDetailsObjects });
  },
  serviceLocationDetails: emptyServiceLocationDetails,
  serviceAssetDetails: emptyServiceAssetDetails,
  isPhotoLocationStored: false,
  updateServiceAssetDetails: (serviceAssetDetails) =>
    set(() => ({ serviceAssetDetails })),
  updateIsPhotoLocationStored: (isPhotoLocationStored) =>
    set(() => ({ isPhotoLocationStored })),
  fileWithLoadedCoordinates: '',
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
    set(() => ({ fileWithLoadedCoordinates })),
  updateServiceLocationDetails: (serviceLocationDetails) =>
    set(() => ({ serviceLocationDetails })),
  updateUploadedImagesBlobs: (uploadedImagesBlob) =>
    set(() => ({ uploadedImagesBlob })),
  updateConfirmation: (confirmation: CaseIDProps | undefined) =>
    set(() => ({ confirmation })),

  // for QR code image
  uploadedImagesQR: [],
  uploadedImagesFileNameQR: [],
  uploadedImagesFileTypeQR: [],
  uploadedImagesBlobQR: [],
  fileDetailsObjectsQR: [],
  updateUploadedImagesQR: (uploadedFilePartsQR: PartitionFileObjectsTypeQR) => {
    const {
      uploadedImagesQR,
      uploadedImagesFileNameQR,
      uploadedImagesFileTypeQR,
      uploadedImagesBlobQR,
      geoDataArraysQR,
    } = uploadedFilePartsQR;
    const fileDetailsObjectsQR = uploadedImagesFileNameQR.map(
      (filename, index) => ({
        name: filename,
        path: uploadedImagesBlobQR[index].blob_uri,
      }),
    );
    const { serviceLocationDetailsQR, updateFileWithLoadedCoordinatesQR } =
      get();
    // update the service location details with the first geo data found
    if (
      !serviceLocationDetailsQR.latitude &&
      !serviceLocationDetailsQR.longitude &&
      geoDataArraysQR.length > 0
    ) {
      geoDataArraysQR.some((geoDataQR) => {
        if (
          geoDataQR.latitude !== null &&
          geoDataQR.longitude !== null &&
          geoDataQR.fileName !== null
        ) {
          updateFileWithLoadedCoordinatesQR(geoDataQR.fileName);
          set((state) => ({
            serviceLocationDetailsQR: {
              ...state.serviceLocationDetailsQR,
              latitude: geoDataQR.latitude,
              longitude: geoDataQR.longitude,
            },
          }));
          // break the loop if the first geo data is found
          return true;
        }
        // continue the loop
        return false;
      });
    }
    // update the service location details with the first geo data found
    set({ uploadedImagesQR });
    set({ uploadedImagesFileNameQR });
    set({ uploadedImagesFileTypeQR });
    set({ uploadedImagesBlobQR });
    set({ fileDetailsObjectsQR });
  },
  serviceLocationDetailsQR: emptyServiceLocationDetails,
  isPhotoLocationStoredQR: false,
  updateIsPhotoLocationStoredQR: (isPhotoLocationStoredQR) =>
    set(() => ({ isPhotoLocationStoredQR })),
  fileWithLoadedCoordinatesQR: '',
  updateFileWithLoadedCoordinatesQR: (fileWithLoadedCoordinatesQR: string) =>
    set(() => ({ fileWithLoadedCoordinatesQR })),
  updateServiceLocationDetailsQR: (serviceLocationDetailsQR) =>
    set(() => ({ serviceLocationDetailsQR })),
  updateUploadedImagesBlobsQR: (uploadedImagesBlobQR) =>
    set(() => ({ uploadedImagesBlobQR })),
  receiveConfirmationEmail: '',
  updateReceiveConfirmationEmail: (receiveConfirmationEmail) =>
    set(() => ({ receiveConfirmationEmail })),
  caseReferenceId: '',
  updateCaseReferenceId: (caseReferenceId) => set(() => ({ caseReferenceId })),
  slaText: '',
  updateSlaText: (slaText) => set(() => ({ slaText })),
  wantConfirmationEmail: '',
  resetConfirmationInformation: () =>
    set(() => ({
      slaText: '',
      caseReferenceId: '',
      wantConfirmationEmail: '',
      receiveConfirmationEmail: '',
      confirmation: undefined,
    })),
  resetBikesDetails: () =>
    set(() => ({
      hireCompaniesOptions: [],
      uploadedImages: [],
      uploadedImagesFileName: [],
      uploadedImagesFileType: [],
      uploadedImagesBlob: [],
      fileDetailsObjects: [],
      serviceAssetDetails: emptyServiceAssetDetails,
      serviceLocationDetails: emptyServiceLocationDetails,
      ownershipType: '',
      obstructionType: '',
      tellUsMoreText: '',
      fileWithLoadedCoordinates: '',
      isPhotoLocationStored: false,
      isPhotoLocationStoredQR: false,
    })),
});
